import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchClasses, fetchRoster, saveBulk, todayISO, addDays, monthRange, weekRange, datesInRange, isSunday
} from './attendanceApi';
import { statusOf } from './AttendanceGrid';

export const GLOBAL_ROLES = ['teacher_admin_level1', 'admin1', 'admin', 'principal', 'teacher'];

// All state + server interaction for the master attendance screen.
export default function useAttendance(user) {
  const isGlobal = GLOBAL_ROLES.includes(user.role);
  const today = todayISO();

  const [classes, setClasses] = useState([]);
  // Default to the logged-in teacher's own class; they can still switch (substitute duty).
  const [cls, setCls] = useState(user.class || '');
  const [mode, setMode] = useState('daily');
  const [anchor, setAnchor] = useState(today);
  const [roster, setRoster] = useState(null);
  const [draft, setDraft] = useState({});     // draft[studentId][date] = status
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(null);   // date saved in daily mode -> reveals WhatsApp bar
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const [from, to] = useMemo(
    () => (mode === 'daily' ? [anchor, anchor] : mode === 'weekly' ? weekRange(anchor) : monthRange(anchor)),
    [mode, anchor]
  );
  const dates = useMemo(() => datesInRange(from, to), [from, to]);

  useEffect(() => {
    if (!isGlobal) return;
    fetchClasses(user).then((c) => { setClasses(c); setCls((cur) => cur || (c.includes(user.class) ? user.class : c[0]) || ''); }).catch(() => {});
  }, [user, isGlobal]);

  const load = useCallback(async () => {
    if (!cls) return;
    setLoading(true); setMsg(''); setSaved(null);
    try {
      const data = await fetchRoster(user, cls, from, to);
      setRoster(data);
      setDraft(data.records);   // saved records; anything unmarked defaults to "present"
      setDirty(false);
    } catch (e) {
      setMsg(e.response?.data?.error || 'Could not load attendance');
    } finally { setLoading(false); }
  }, [user, cls, from, to]);

  useEffect(() => { load(); }, [load]);

  const workDates = useMemo(
    () => (roster ? dates.filter((d) => !isSunday(d) && !roster.holidays[d] && d <= today) : []),
    [dates, roster, today]
  );

  const change = (sid, date, status) => {
    setDraft((p) => ({ ...p, [sid]: { ...(p[sid] || {}), [date]: status } }));
    setDirty(true); setSaved(null);
  };

  const markAll = (status) => {
    if (!roster) return;
    const next = { ...draft };
    roster.students.forEach((s) => {
      workDates.forEach((d) => { next[s._id] = { ...(next[s._id] || {}), [d]: status }; });
    });
    setDraft(next); setDirty(true); setSaved(null);
  };

  const save = async () => {
    if (!roster) return;
    if (!workDates.length) { setMsg('No working days in this range.'); return; }
    const entries = workDates.map((date) => ({
      date,
      marks: Object.fromEntries(roster.students.map((s) => [s._id, statusOf(draft, s._id, date)]))
    }));
    try {
      const r = await saveBulk(user, cls, entries);
      setMsg(`Saved. ${r.written} updated, ${r.unchanged} unchanged.`);
      await load();
      if (mode === 'daily') setSaved(anchor);   // after load() so it is not cleared
    } catch (e) {
      setMsg(e.response?.data?.error || 'Save failed');
    }
  };

  const step = (dir) => {
    if (mode === 'daily') setAnchor(addDays(anchor, dir));
    else if (mode === 'weekly') setAnchor(addDays(anchor, 7 * dir));
    else {
      const d = new Date(`${anchor}T00:00:00`);
      d.setMonth(d.getMonth() + dir, 1);
      setAnchor(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
    }
  };

  const counts = useMemo(() => {
    const c = { present: 0, absent: 0, leave: 0, late: 0 };
    if (roster && mode === 'daily') roster.students.forEach((s) => { c[statusOf(draft, s._id, anchor)]++; });
    return c;
  }, [roster, draft, mode, anchor]);

  return {
    isGlobal, today, classes, cls, setCls, mode, setMode, anchor, setAnchor, from, to, dates,
    roster, draft, dirty, saved, msg, loading, counts, load, change, markAll, save, step
  };
}
